import { useEffect, useState } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi } from "@/lib/api";
import type { Provider, ProviderModel } from "@/lib/types";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Edit2, X, Plus, Trash2, ArrowLeft } from "lucide-react";
import { Link, useParams } from "wouter";

type ModalMode =
  | { type: "closed" }
  | { type: "create" }
  | { type: "edit"; model: ProviderModel };

export default function AdminProviderModels() {
  const { t } = useLanguage();
  const params = useParams<{ id: string }>();
  const providerId = params.id;
  const [provider, setProvider] = useState<Provider | null>(null);
  const [models, setModels] = useState<ProviderModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalMode>({ type: "closed" });

  const refresh = async () => {
    if (!providerId) return;
    try {
      // In a real app we might fetch the specific provider, 
      // here we fetch all and find it, or we assume models endpoint works.
      const [providersData, modelsData] = await Promise.all([
        adminApi.getProviders(),
        adminApi.getProviderModels(providerId)
      ]);
      const p = providersData.find((x) => x.id === providerId);
      if (p) setProvider(p);
      setModels(modelsData);
    } catch (e: any) {
      toast.error(e.message || "Failed to load models");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [providerId]);

  const handleDeactivate = async (modelId: string) => {
    if (!providerId) return;
    if (!window.confirm("Are you sure you want to deactivate this model?")) return;
    try {
      await adminApi.deactivateProviderModel(providerId, modelId);
      toast.success("Model deactivated");
      refresh();
    } catch (e: any) {
      toast.error(e.message || "Failed to deactivate");
    }
  };

  if (!providerId) return <ConsoleLayout title="Provider Models"><div>Invalid Provider ID</div></ConsoleLayout>;

  return (
    <ConsoleLayout title="Provider Models">
      <div className="flex flex-col h-full bg-background relative">
        <div className="p-6 overflow-auto">
          <div className="mb-4">
            <Link href="/console/admin/providers">
              <Button variant="ghost" size="sm" className="-ml-3 text-muted-foreground">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Providers
              </Button>
            </Link>
          </div>

          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-foreground">
                Models for {provider ? provider.name : "Provider"}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage the underlying language models available from this provider.
              </p>
            </div>
            <Button onClick={() => setModal({ type: "create" })}>
              <Plus className="w-4 h-4 mr-2" />
              New Model
            </Button>
          </div>

          <div className="border border-border rounded-xl bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-muted/50 text-muted-foreground border-b border-border">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Upstream ID</th>
                    <th className="px-4 py-3 font-medium">Context</th>
                    <th className="px-4 py-3 font-medium">{t("admin.providerModels.rpmLimit")}</th>
                    <th className="px-4 py-3 font-medium">{t("admin.providerModels.tpmLimit")}</th>
                    <th className="px-4 py-3 font-medium">Protocol</th>
                    <th className="px-4 py-3 font-medium">Costs (In/Out/Cw/Cr)</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                        Loading...
                      </td>
                    </tr>
                  ) : models.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                        No models found for this provider.
                      </td>
                    </tr>
                  ) : (
                    models.map((m) => (
                      <tr key={m.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground">
                          {m.name}
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                            {m.upstream_model_name}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {m.context_window}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {m.rpm_limit ?? <span className="italic">{t("admin.providerModels.unlimited")}</span>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {m.tpm_limit ?? <span className="italic">{t("admin.providerModels.unlimited")}</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold">
                            {m.protocol}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground tabular-nums text-xs">
                          {m.input_cost_per_ktok} / {m.output_cost_per_ktok} / {m.cache_write_cost_per_ktok} / {m.cache_read_cost_per_ktok}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-full ${
                              m.is_active
                                ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50"
                                : "text-muted-foreground bg-muted"
                            }`}
                          >
                            {m.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setModal({ type: "edit", model: m })}
                            >
                              <Edit2 className="w-4 h-4 text-muted-foreground" />
                            </Button>
                            {m.is_active && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeactivate(m.id)}
                              >
                                <Trash2 className="w-4 h-4 text-destructive/70 hover:text-destructive" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {modal.type !== "closed" && provider && (
        <div className="absolute inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border shadow-xl rounded-xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/20 shrink-0">
              <h3 className="font-semibold text-lg">
                {modal.type === "create" && "New Model"}
                {modal.type === "edit" && "Edit Model"}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setModal({ type: "closed" })}
                className="-mr-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="overflow-y-auto shrink p-6">
              <ModelForm
                mode={modal.type}
                provider={provider}
                model={modal.type === "edit" ? modal.model : undefined}
                onSuccess={() => {
                  setModal({ type: "closed" });
                  refresh();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </ConsoleLayout>
  );
}

function ModelForm({
  mode,
  provider,
  model,
  onSuccess,
}: {
  mode: "create" | "edit";
  provider: Provider;
  model?: ProviderModel;
  onSuccess: () => void;
}) {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const rpmLimitStr = fd.get("rpm_limit") as string;
    const tpmLimitStr = fd.get("tpm_limit") as string;
    const data = {
      name: fd.get("name") as string,
      upstream_model_name: fd.get("upstream_model_name") as string,
      context_window: parseInt(fd.get("context_window") as string, 10),
      rpm_limit: rpmLimitStr ? parseInt(rpmLimitStr, 10) : null,
      tpm_limit: tpmLimitStr ? parseInt(tpmLimitStr, 10) : null,
      protocol: provider.protocol,
      input_cost_per_ktok: parseFloat(fd.get("input_cost_per_ktok") as string),
      output_cost_per_ktok: parseFloat(fd.get("output_cost_per_ktok") as string),
      cache_write_cost_per_ktok: parseFloat(fd.get("cache_write_cost_per_ktok") as string),
      cache_read_cost_per_ktok: parseFloat(fd.get("cache_read_cost_per_ktok") as string),
    };

    setSubmitting(true);
    try {
      if (mode === "create") {
        await adminApi.createProviderModel(provider.id, data);
        toast.success("Model created");
      } else {
        await adminApi.updateProviderModel(provider.id, model!.id, data);
        toast.success("Model updated");
      }
      onSuccess();
    } catch (err: any) {
      toast.error(err.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input name="name" defaultValue={model?.name} required placeholder="e.g. GPT-4o" />
        </div>
        <div className="space-y-2">
          <Label>Upstream Model Name</Label>
          <Input
            name="upstream_model_name"
            defaultValue={model?.upstream_model_name}
            required
            placeholder="e.g. gpt-4o-2024-08-06"
            className="font-mono text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Context Window</Label>
          <Input
            name="context_window"
            type="number"
            defaultValue={model?.context_window || 128000}
            required
            min={1}
          />
        </div>
        <div className="space-y-2">
          <Label>Protocol</Label>
          <div className="px-3 py-2 text-sm rounded-md border border-input bg-muted/40 text-muted-foreground uppercase tracking-wide font-semibold">
            {provider.protocol}
          </div>
          <p className="text-xs text-muted-foreground">Inherited from the provider.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t("admin.providerModels.rpmLimit")}</Label>
          <Input
            name="rpm_limit"
            type="number"
            defaultValue={model?.rpm_limit ?? ""}
            min={0}
            placeholder={t("admin.providerModels.unlimited")}
          />
          <p className="text-xs text-muted-foreground">{t("admin.providerModels.quotaHelp")}</p>
        </div>
        <div className="space-y-2">
          <Label>{t("admin.providerModels.tpmLimit")}</Label>
          <Input
            name="tpm_limit"
            type="number"
            defaultValue={model?.tpm_limit ?? ""}
            min={0}
            placeholder={t("admin.providerModels.unlimited")}
          />
          <p className="text-xs text-muted-foreground">{t("admin.providerModels.quotaHelp")}</p>
        </div>
      </div>

      <div className="space-y-3">
        <Label>Upstream Cost (per 1k tokens)</Label>
        <div className="grid grid-cols-4 gap-4 p-4 border border-border rounded-lg bg-muted/20">
          <div className="space-y-2">
            <Label className="text-xs">Input</Label>
            <Input name="input_cost_per_ktok" type="number" step="0.000000000000000001" required defaultValue={model?.input_cost_per_ktok ?? 0} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Output</Label>
            <Input name="output_cost_per_ktok" type="number" step="0.000000000000000001" required defaultValue={model?.output_cost_per_ktok ?? 0} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Cache Write</Label>
            <Input name="cache_write_cost_per_ktok" type="number" step="0.000000000000000001" required defaultValue={model?.cache_write_cost_per_ktok ?? 0} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Cache Read</Label>
            <Input name="cache_read_cost_per_ktok" type="number" step="0.000000000000000001" required defaultValue={model?.cache_read_cost_per_ktok ?? 0} />
          </div>
        </div>
      </div>

      <div className="pt-4 flex justify-end gap-2 border-t border-border mt-6">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving..." : mode === "create" ? "Create Model" : "Save Changes"}
        </Button>
      </div>
    </form>
  );
}
